import { useState } from 'react';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import HelpIcon from '@mui/icons-material/Help';
import { Box, ButtonBase, MenuItem, Tooltip } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { countries, getCountryCode } from 'countries-list';
import validator from 'validator';

import { StyledCustomLoader } from '~/components/ui/CustomLoader';
import { StyledTab, StyledTabs } from '~/components/ui/StyledTabs';
import { Text } from '~/components/ui/text/Text';
import TextInput from '~/components/ui/TextInput';
import { GOOGLE_MAP_KEY } from '~/constants/common';
import { getIsUpdatingBillingInformation, getLoggedInUser } from '~/store/reducers/account/selector';
import { COLORS } from '~/styles/colors';
import { isMaxLength } from '~/utils/common';
import { parsePlaceDetails } from '~/utils/google-maps/adress';

import * as S from '../styles';

const MAX_LENGTH = 250;
type BillingInformationProps = {
  isPayment: boolean;
  handleChangeTaxInfo: (value: any) => void;
  handleChangeTaxType: (value: any) => void;
  data: any;
};
function BillingInformation({ isPayment, data, handleChangeTaxInfo, handleChangeTaxType }: BillingInformationProps) {
  const { t } = useTranslation();
  const { data: settingsData, updated } = useSelector(getLoggedInUser);
  const isUpdating = useSelector(getIsUpdatingBillingInformation);

  const countriesList = Object.values(countries);

  const [isUpdate, setIsUpdate] = useState(false);
  const [company, setCompany] = useState(data?.company || '');
  const [nif, setNif] = useState(data?.nifCif || '');
  const [country, setCountry] = useState(data?.country || '');
  const [firstName, setFirstName] = useState(data?.firstName || '');
  const [lastName, setLastName] = useState(data?.lastName || '');
  const [address, setAddress] = useState(data?.address || '');
  const [city, setCity] = useState(data?.city || '');
  const [postCode, setPostCode] = useState(data?.postalCode || '');
  const [email, setEmail] = useState(data?.email || '');
  const [initialData, setInitialData] = useState({
    type: data?.type || '',
    company: data?.company || '',
    nif: data?.nifCif || '',
    country: data?.country || '',
    firstName: data?.firstName || '',
    lastName: data?.lastName || '',
    address: data?.address || '',
    city: data?.city || '',
    postCode: data?.postalCode || '',
    email: data?.email || '',
  });

  const { placesService, placePredictions, getPlacePredictions } = usePlacesService({
    apiKey: GOOGLE_MAP_KEY,
    language: settingsData?.languageCode || undefined,
  });

  const isChanged = () => {
    return (
      company !== initialData.company ||
      nif !== initialData.nif ||
      country !== initialData.country ||
      firstName !== initialData.firstName ||
      lastName !== initialData.lastName ||
      address !== initialData.address ||
      city !== initialData.city ||
      postCode !== initialData.postCode ||
      email !== initialData.email
    );
  };

  const isValid =
    (data?.type === 'company' && !company) ||
    !nif ||
    !firstName ||
    !lastName ||
    !address ||
    !city ||
    !postCode ||
    !email ||
    !validator.isEmail(email);

  const handleSave = () => {
    if (isValid) return;
    setIsUpdate(true);

    handleChangeTaxInfo({
      company,
      nif_cif: nif,
      country,
      first_name: firstName,
      last_name: lastName,
      address,
      city,
      postal_code: postCode,
      email,
    });
    setInitialData({
      company,
      nif,
      country,
      firstName,
      lastName,
      address,
      city,
      postCode,
      email,
    });
  };

  const handlePreselectPlaceDetails = (placeDetails) => {
    const { city, country, postalCode } = parsePlaceDetails(placeDetails);

    setCity(city);
    setPostCode(postalCode);
    setCountry(country);
  };

  const handlePlaceChange = (_, value) => {
    const selectedPlace = placePredictions.find((item) => item.description === value);
    setAddress(selectedPlace.description);

    placesService?.getDetails(
      {
        placeId: selectedPlace.place_id,
      },
      (placeDetails) => handlePreselectPlaceDetails(placeDetails),
    );
  };

  const isIndividual = data?.type === 'individual';

  return (
    <>
      <Box display="flex" flexDirection="column" flex={1} sx={{ padding: '16px' }}>
        <StyledTabs sx={{ border: `1px solid ${COLORS.grey200}` }}>
          <StyledTab
            onClick={() => {
              setIsUpdate(false);
              handleChangeTaxType('type', 'individual');
            }}
            $isActive={isIndividual}
          >
            {t('settings:individualBtn')}
          </StyledTab>
          <StyledTab
            onClick={() => {
              setIsUpdate(false);
              handleChangeTaxType('type', 'company');
            }}
            $isActive={!isIndividual}
          >
            {t('settings:companyBtn')}
          </StyledTab>
        </StyledTabs>

        <Box sx={S.billingFields}>
          <TextInput
            fullWidth
            label={`${t('settings:companyBtn')} ${isIndividual ? t('settings:optional') : ''}`}
            defaultValue={company}
            placeholder={t('settings:placeholdCompany')}
            value={company}
            error={!isIndividual && !company && isChanged()}
            onChange={(e) => setCompany(e.target.value)}
            helperText={isMaxLength(company, MAX_LENGTH) || (!company && isChanged() && t('inputs:required'))}
            inputProps={{ maxLength: MAX_LENGTH }}
          />
          <TextInput
            fullWidth
            label={t('settings:nif')}
            defaultValue={nif}
            placeholder={t('settings:placeholdNIF')}
            value={nif}
            onChange={(e) => setNif(e.target.value)}
            error={!nif && isChanged()}
            helperText={isMaxLength(nif, MAX_LENGTH) || (!nif && isChanged() && t('inputs:required'))}
            inputProps={{ maxLength: MAX_LENGTH }}
            InputProps={{
              endAdornment: (
                <Tooltip
                  title={
                    <Text variant="input15" sx={{ color: 'white' }}>
                      {t('alerts:nif_info')}
                    </Text>
                  }
                  arrow
                  slotProps={{
                    tooltip: { sx: { backgroundColor: '#313031', borderRadius: '8px' } },
                  }}
                >
                  <HelpIcon fontSize="small" />
                </Tooltip>
              ),
            }}
          />
          <TextInput
            fullWidth
            label={t('settings:name')}
            defaultValue={firstName}
            value={firstName}
            placeholder={t('settings:placeholdName')}
            onChange={(e) => setFirstName(e.target.value)}
            error={!firstName && isChanged()}
            helperText={isMaxLength(firstName, 70) || (!firstName && isChanged() && t('inputs:required'))}
            inputProps={{ maxLength: 70 }}
          />
          <TextInput
            fullWidth
            label={t('settings:surname')}
            placeholder={t('settings:placeholdSurname')}
            defaultValue={lastName}
            onChange={(e) => setLastName(e.target.value)}
            error={!lastName && isChanged()}
            helperText={isMaxLength(lastName, 70) || (!lastName && isChanged() && t('inputs:required'))}
            inputProps={{ maxLength: 70 }}
          />
          <Autocomplete
            freeSolo
            disableClearable
            options={placePredictions.map((option) => option.description)}
            onChange={handlePlaceChange}
            defaultValue={address}
            value={address}
            renderInput={(params) => (
              <TextInput
                {...params}
                label={t('settings:adress')}
                size="small"
                placeholder={t('settings:placeholdAddress')}
                onChange={(evt) => {
                  getPlacePredictions({
                    input: evt.target.value,
                    componentRestrictions: { country: getCountryCode(country) },
                  });
                  setAddress(evt.target.value);
                }}
                error={!address && isChanged()}
                helperText={!address && isChanged() && t('inputs:required')}
                InputProps={{
                  ...params.InputProps,
                  type: 'search',
                }}
              />
            )}
          />
          <TextInput
            fullWidth
            select
            value={country || 'United States'}
            defaultValue={country}
            onChange={(e) => setCountry(e.target.value)}
            label={t('settings:country')}
            SelectProps={{
              MenuProps: {
                sx: { height: '50%' },
              },
            }}
          >
            {countriesList.map((option) => (
              <MenuItem key={option.name} value={option.name}>
                {option.name + ' | ' + option.native}
              </MenuItem>
            ))}
          </TextInput>
          <TextInput
            fullWidth
            label={t('settings:postCode')}
            placeholder={t('settings:placeholdPost')}
            defaultValue={postCode}
            value={postCode}
            error={!postCode && isChanged()}
            helperText={isMaxLength(postCode, MAX_LENGTH) || (!postCode && isChanged() && t('inputs:required'))}
            inputProps={{ maxLength: MAX_LENGTH }}
            onChange={(e) => setPostCode(e.target.value)}
          />
          <TextInput
            fullWidth
            label={t('settings:city')}
            placeholder={t('settings:placeholdCity')}
            value={city}
            defaultValue={city}
            error={!city && isChanged()}
            helperText={isMaxLength(city, MAX_LENGTH) || (!city && isChanged() && t('inputs:required'))}
            inputProps={{ maxLength: MAX_LENGTH }}
            onChange={(e) => setCity(e.target.value)}
          />
          <TextInput
            containerSx={{ gridColumn: 'span 2' }}
            fullWidth
            placeholder={t('settings:placeholdEmail')}
            label={t('settings:email')}
            defaultValue={email}
            error={(!email && isChanged()) || (!validator.isEmail(email) && isChanged())}
            helperText={
              isMaxLength(email, MAX_LENGTH) ||
              (!email && isChanged() && t('inputs:required')) ||
              (!validator.isEmail(email) && isChanged() && t('errors:emailIsValid'))
            }
            inputProps={{ maxLength: MAX_LENGTH }}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Box>
        <Box display="flex" justifyContent="flex-end" padding={2}>
          <ButtonBase sx={S.updateBtn} className="static" disabled={!isChanged() || isValid} onClick={handleSave}>
            {isUpdating ? <StyledCustomLoader /> : !isPayment ? t('settings:saveBtn') : t('checkout:nextBtn')}
          </ButtonBase>
        </Box>
        {updated && isUpdate && !isChanged() ? (
          <Box sx={S.updatedText}>
            <CheckCircleOutlineRoundedIcon htmlColor="var(--greenMain)" />
            <Text variant="button14Med" sx={{ color: 'var(--greenMain)' }}>
              {t('settings:informationUpdated')}
            </Text>
          </Box>
        ) : null}
      </Box>
    </>
  );
}

export default BillingInformation;
